import { motion, useAnimationControls } from 'framer-motion'
import { createContext, CSSProperties, Dispatch, ReactNode, SetStateAction, useEffect, useRef, useState } from 'react'
import { useContext } from 'react'
import styles from '../../assets/scss/components/home/home.module.scss'
import { BreakpointsContext } from '../layout/breakpoints-provider.client'
import { classNames } from '@shopify/css-utilities'
import { useHome } from '../../lib/hooks/home.client'

const additionalImagesLoaded: { [src: string]: boolean } = {}

interface LoaderContextProps {
  isLoaded: boolean
  setIsLoaded: Dispatch<SetStateAction<boolean>>
}

const LoaderContext = createContext({} as LoaderContextProps)

export const AssetLoader: React.FC<{
  additionalImages?: string[]
  children: ReactNode
  defaultIsLoaded?: boolean
  rect?: DOMRect
  style?: Pick<CSSProperties, 'zIndex'>
}> = ({ additionalImages = [], children, defaultIsLoaded = false, rect, style }) => {
  const { isLargeUp } = useContext(BreakpointsContext)
  const { canvasHeight, canvasWidth } = useHome()
  const [isLoaded, setIsLoaded] = useState(defaultIsLoaded)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const controls = useAnimationControls()

  useEffect(() => {
    if (rect) {
      const canvasCenter = {
        x: canvasWidth / 2,
        y: canvasHeight / 2,
      }

      const rectCenter = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      }

      setOffset({
        x: canvasCenter.x - rectCenter.x,
        y: canvasCenter.y - rectCenter.y,
      })
    }
  }, [rect])

  useEffect(() => {
    additionalImages.forEach((imageSrc) => {
      const image = new Image()

      image.onload = () => {
        additionalImagesLoaded[imageSrc] = true
      }

      image.src = imageSrc

      additionalImagesLoaded[imageSrc] = image.complete
    })
  }, [])

  const sequence = async () => {
    const delay = Math.random() / 2
    controls.set({ opacity: 0, translate: isLargeUp ? `${offset.x / 2}px ${offset.y / 2}px` : '0px 0px' })

    controls.start({
      opacity: 1,
      transition: { delay: delay + 0.5, duration: 0.4, ease: [0, 0, 0, 1] },
    })

    controls.start({
      translate: '0px 0px',
      transition: { delay: delay, duration: 1.5, ease: [0, 0, 0, 1] },
    })
  }

  useEffect(() => {
    const isEverythingLoaded =
      additionalImages.every((additionalImage) => Object.keys(additionalImagesLoaded).includes(additionalImage)) &&
      isLoaded

    if (isEverythingLoaded) {
      sequence()
    }
  }, [isLoaded, additionalImagesLoaded])

  return (
    <LoaderContext.Provider value={{ isLoaded, setIsLoaded }}>
      <motion.div animate={controls} className={styles.assetLoader} initial={{ ...style, opacity: 0 }}>
        {children}
      </motion.div>
    </LoaderContext.Provider>
  )
}

export const AssetLoaderImage: React.FC<{
  alt: string
  className?: string
  height: string | number
  src: string
  width: string | number
}> = (props) => {
  const { setIsLoaded } = useContext(LoaderContext)
  const ref = useRef<HTMLImageElement>(null)

  useEffect(() => {
    if (ref.current) {
      setIsLoaded(ref.current.complete)
    }
  }, [ref])

  return <img {...props} decoding="async" loading="lazy" onLoad={() => setIsLoaded(true)} ref={ref} />
}

export const StaticAssetLoader: React.FC<{
  alt?: string
  className?: string
  height: string | number
  src: string
  style?: Pick<CSSProperties, 'zIndex'>
  width: string | number
}> = ({ className, style, ...props }) => {
  const { isLargeUp } = useContext(BreakpointsContext)
  const { canvasHeight, canvasWidth } = useHome()
  const [isLoaded, setIsLoaded] = useState(false)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const ref = useRef<HTMLImageElement>(null)
  const controls = useAnimationControls()

  useEffect(() => {
    if (ref.current) {
      const canvasCenter = {
        x: canvasWidth / 2,
        y: canvasHeight / 2,
      }

      const rect = ref.current.getBoundingClientRect()
      const rectCenter = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      }

      setOffset({
        x: canvasCenter.x - rectCenter.x,
        y: canvasCenter.y - rectCenter.y,
      })

      setIsLoaded(ref.current.complete)
    }
  }, [ref])

  const sequence = async () => {
    const delay = Math.random() / 2
    controls.set({ opacity: 0, translate: isLargeUp ? `${offset.x / 2}px ${offset.y / 2}px` : '0px 0px' })

    controls.start({
      opacity: 1,
      transition: { delay: delay + 0.5, duration: 0.4, ease: [0, 0, 0, 1] },
    })

    controls.start({
      translate: '0px 0px',
      transition: { delay: delay, duration: 1, ease: [0, 0, 0, 1] },
    })
  }

  useEffect(() => {
    if (isLoaded) {
      sequence()
    }
  }, [isLoaded])

  return (
    <motion.div
      animate={controls}
      className={classNames(styles.assetLoader, className)}
      initial={{ ...style, opacity: 0 }}>
      <img {...props} decoding="async" loading="lazy" onLoad={() => setIsLoaded(true)} ref={ref} />
    </motion.div>
  )
}
